<template>
  <div>
    <v-menu offset-y internal-activator close-on-click v-model="active">
      <template v-slot:activator="{ on, attrs, value }">
        <div v-bind="attrs" v-on="on" :class="{ activation: true, 'activation-active': value }">
          <div class="activation-header text-center">
            <v-icon size="10px">mdi-map-marker-radius</v-icon>
            <span class="ml-1">Self Check-in</span>
          </div>
          <div class="activation-footer text-center">
            <span
              v-if="notifications.length === 0"
              class="font-weight-regular grey--text text--darken-2">
              No arrivals today
            </span>
            <span
              v-else-if="unreadNotifications.length === 0"
              class="font-weight-regular grey--text text--darken-3">
              <strong>{{ notifications.length }}</strong> arrival{{
                notifications.length > 1 ? 's' : ''
              }}
              today
            </span>
            <v-icon
              size="10px"
              class="circular-pulse"
              color="#FF9A1A"
              v-if="unreadNotifications.length > 0">
              mdi-circle
            </v-icon>
            <span v-if="unreadNotifications.length > 0">{{ unreadNotifications.length }} NEW</span>
          </div>
        </div>
      </template>
      <v-card class="min-w">
        <v-card-title
          class="pb-0 d-flex flex-row justify-space-between align-content-center align-center">
          <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2 lh-normal">
            CHECKED-IN TODAY
          </span>
          <v-btn
            text
            @click="acknowledgeAll()"
            class="pa-0 text-initial h-auto black--text lh-normal"
            :disabled="unreadNotifications.length === 0 || acknowledgementInProcess">
            Mark all as read
          </v-btn>
        </v-card-title>
        <v-list v-if="notifications.length > 0">
          <v-list-item v-for="appointment in notifications" :key="appointment.id">
            <div class="d-flex flex-row justify-space-between w-full">
              <div @click="handleClickEvent(appointment)" class="cursor-pointer w-full">
                <v-list-item-title>{{ appointment.user.company.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  Arrived at
                  {{
                    novaCore.formatDateTimeWithMilitarySupport(
                      appointment.statusTimeline.Arrived,
                      warehouse.timezone,
                      novaCore.LuxonDateTimeFormats.Extended12HrTimeAMPM,
                      $isMilitaryTimeEnabled($org),
                      novaCore.LuxonDateTimeFormats.Extended24HrTime
                    )
                  }}
                  <v-icon size="12px"> mdi-arrow-right</v-icon>
                  <span v-html="renderOnTimeOrLateLabel(appointment)"></span>
                </v-list-item-subtitle>
              </div>
              <div class="px-4" v-if="!appointment.checkInAcknowledged">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon
                      v-on="on"
                      size="12px"
                      color="#FF9A1A"
                      @click="acknowledgeAppointment(appointment)"
                      :disabled="acknowledgementInProcess">
                      mdi-circle
                    </v-icon>
                  </template>
                  Mark as read
                </v-tooltip>
              </div>
            </div>
          </v-list-item>
        </v-list>
        <v-card-actions
          class="px-8 pt-0 pb-4 grey--text text-center"
          v-if="notifications.length > 0">
          These were all the self check-ins done today
        </v-card-actions>
        <v-card-actions class="px-8 py-14 grey--text text-center" v-else>
          There were no self check-ins done today
        </v-card-actions>
      </v-card>
    </v-menu>
    <appointment-details-dialog
      v-if="selectedEvent"
      :external-activator="true"
      :show-dialog="showDetailsDialog"
      @close="handleDetailsDialogClose"
      :appointment="selectedEvent">
    </appointment-details-dialog>
  </div>
</template>

<script>
import moment from 'moment-timezone';

/**
 * @displayName Check-in Notification Dropdown
 */
export default {
  props: {
    appointments: {
      type: Array,
      default: () => []
    },
    warehouse: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      active: false,
      acknowledgementInProcess: false,
      selectedEvent: null,
      showDetailsDialog: true
    };
  },
  methods: {
    handleClickEvent(appt) {
      this.$store.commit('Calendar/setSelectedEvent', appt);
      this.$router.replace({ query: { appointmentId: appt.id } });
      this.selectedEvent = appt;
      this.showDetailsDialog = true;
    },
    handleDetailsDialogClose() {
      this.$store.commit('Calendar/setSelectedEvent', null);
      this.showDetailsDialog = false;
    },
    async acknowledgeAppointment(appt, trackMPEvent = true) {
      this.acknowledgementInProcess = true;
      try {
        await axios.patch(`appointment/${appt.id}`, {
          checkInAcknowledged: true
        });
      } finally {
        this.acknowledgementInProcess = false;
        if (trackMPEvent) {
          this.trackMixPanelEventMarkAsRead('Single item');
        }
      }
    },
    async acknowledgeAll() {
      this.trackMixPanelEventMarkAsRead('All items');
      return Promise.all(
        this.unreadNotifications.map(appt => this.acknowledgeAppointment(appt, false))
      );
    },
    isApptToday(appt) {
      const todayDate = moment().tz(this.warehouse.timezone).format('YYYY-MM-DD');
      const apptStartDate = moment(appt.start)
        .tz(this.warehouse.timezone, true)
        .format('YYYY-MM-DD');
      return todayDate === apptStartDate;
    },
    renderOnTimeOrLateLabel(appt) {
      const readableDuration = this.util.getReadableDuration(
        appt.start,
        appt.statusTimeline.Arrived,
        this.warehouse.timezone,
        ' LATE'
      );
      const className = readableDuration.toLowerCase().includes('late')
        ? 'error--text'
        : 'success--text';
      return `
      <span class='${className} font-weight-bold text--darken-2'>
        ${readableDuration.toUpperCase()}
      </span>
      `;
    },
    async trackMixPanelEventMarkAsRead(item) {
      await this.mixpanel?.track(this.mixpanel.events.MODULE.SELF_CHECK_IN.MARK_AS_READ, {
        'Warehouse ID': this.warehouse.id,
        'Warehouse Name': this.warehouse.name,
        'List Item': item,
        'Org ID': this.$org.id,
        'Org Name': this.$org.name
      });
    },
    async trackMixPanelEventOpenList() {
      const arrivals =
        this.notifications.length === 0
          ? 'no arrivals'
          : this.unreadNotifications.length === 0
          ? 'all read'
          : `${this.unreadNotifications.length} new arrival${
              this.unreadNotifications.length === 1 ? '' : 's'
            }`;
      await this.mixpanel?.track(
        this.mixpanel.events.MODULE.SELF_CHECK_IN.OPEN_SELF_CHECK_IN_LIST,
        {
          'Warehouse ID': this.warehouse.id,
          'Warehouse Name': this.warehouse.name,
          'Self Check-in Arrivals': arrivals,
          'Org ID': this.$org.id,
          'Org Name': this.$org.name
        }
      );
    }
  },
  watch: {
    active(value) {
      if (value) {
        this.trackMixPanelEventOpenList();
      }
    }
  },
  computed: {
    notifications() {
      return this.appointments
        .filter(
          appt => appt.statusTimeline.Arrived && appt.isCheckedInByCarrier && this.isApptToday(appt)
        )
        .sort((a, b) => {
          return b.statusTimeline.Arrived.localeCompare(a.statusTimeline.Arrived);
        });
    },
    unreadNotifications() {
      return this.notifications.filter(appt => !appt.checkInAcknowledged);
    }
  }
};
</script>

<style scoped>
.h-auto {
  height: auto !important;
}

.text-initial {
  text-transform: initial;
}

.lh-normal {
  line-height: normal;
}

.min-w {
  min-width: 300px;
}

.activation {
  border-radius: 4px;
  border: solid 1px #e0e0e0;
}

.activation-active {
  border: solid 1px #ff9a1a;
}

.activation-header {
  background-color: #eee;
  display: flex;
  height: 24px;
  padding-left: 4px;
  padding-right: 4px;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  font-size: 9px;
}

.activation-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 2px;
  flex: 1 0 0;
  align-self: stretch;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
}

.w-full {
  width: 100%;
}
</style>
